import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Save,
  Refresh,
  SmartToy,
  CloudUploadOutlined,
  Tune,
  Palette,
  ViewList,
  HelpOutline,
  ChatBubbleOutlineOutlined,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getSupportBots,
  getSupportBotByProduct,
  previewSupportBotByProduct,
  uploadSupportBotAvatar,
  updateSupportBotByProduct,
} from '../services/api';
import SupportBotChatSurface from '../../../common/SupportBotChatSurface';
import './BotsPage.css';

const EMPTY_THEME_CONFIG = {
  panel_header_bg: '',
  panel_header_text: '',
  panel_header_subtext: '',
  avatar_bg: '',
  assistant_bg: '',
  assistant_text: '',
  assistant_border: '',
  user_bg: '',
  user_text: '',
  chip_bg: '',
  chip_hover_bg: '',
  chip_text: '',
  chip_border: '',
  input_focus: '',
  send_bg: '',
  send_hover_bg: '',
  send_text: '',
  toggle_bg: '',
  toggle_hover_bg: '',
  toggle_text: '',
  toggle_shadow: '',
  sliver_bg: '',
  sliver_hover_bg: '',
  sliver_shadow_color: '',
  sliver_shadow_hover_color: '',
};

const THEME_COLOR_FIELDS = [
  { key: 'panel_header_bg', label: 'Fundo do cabeçalho', allowGradient: true },
  { key: 'panel_header_text', label: 'Texto do cabeçalho' },
  { key: 'panel_header_subtext', label: 'Subtítulo do cabeçalho' },
  { key: 'avatar_bg', label: 'Fundo do avatar' },
  { key: 'assistant_bg', label: 'Fundo da mensagem do bot', allowGradient: true },
  { key: 'assistant_text', label: 'Texto da mensagem do bot' },
  { key: 'assistant_border', label: 'Borda da mensagem do bot' },
  { key: 'user_bg', label: 'Fundo da mensagem do usuário', allowGradient: true },
  { key: 'user_text', label: 'Texto da mensagem do usuário' },
  { key: 'chip_bg', label: 'Fundo das sugestões', allowGradient: true },
  { key: 'chip_hover_bg', label: 'Fundo das sugestões ao passar o mouse', allowGradient: true },
  { key: 'chip_text', label: 'Texto das sugestões' },
  { key: 'chip_border', label: 'Borda das sugestões' },
  { key: 'input_focus', label: 'Borda do campo em foco' },
  { key: 'send_bg', label: 'Fundo do botão enviar', allowGradient: true },
  { key: 'send_hover_bg', label: 'Fundo do botão enviar ao passar o mouse', allowGradient: true },
  { key: 'send_text', label: 'Texto/ícone do botão enviar' },
  { key: 'toggle_bg', label: 'Fundo do botão flutuante', allowGradient: true },
  { key: 'toggle_hover_bg', label: 'Fundo do botão flutuante ao passar o mouse', allowGradient: true },
  { key: 'toggle_text', label: 'Texto do botão flutuante' },
  { key: 'toggle_shadow', label: 'Sombra do botão flutuante' },
  { key: 'sliver_bg', label: 'Cor da alça lateral fechada', allowGradient: true },
  { key: 'sliver_hover_bg', label: 'Cor da alça lateral ao passar o mouse', allowGradient: true },
  { key: 'sliver_shadow_color', label: 'Sombra da alça lateral' },
  { key: 'sliver_shadow_hover_color', label: 'Sombra da alça lateral ao passar o mouse' },
];

const DEFAULT_HEADER_SUBTITLE = 'Assistente de suporte';
const DEFAULT_TOGGLE_LABEL = 'ajuda?';
const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const MAX_PREVIEW_CHAT_MESSAGES = 30;
const MAX_PREVIEW_HISTORY = 12;
const THEME_CONFIG_CSS_VAR_MAP = {
  panel_header_bg: '--sb-panel-header-bg',
  panel_header_text: '--sb-panel-header-text',
  panel_header_subtext: '--sb-panel-header-subtext',
  avatar_bg: '--sb-avatar-bg',
  assistant_bg: '--sb-assistant-bg',
  assistant_text: '--sb-assistant-text',
  assistant_border: '--sb-assistant-border',
  user_bg: '--sb-user-bg',
  user_text: '--sb-user-text',
  chip_bg: '--sb-chip-bg',
  chip_hover_bg: '--sb-chip-hover-bg',
  chip_text: '--sb-chip-text',
  chip_border: '--sb-chip-border',
  input_focus: '--sb-input-focus',
  send_bg: '--sb-send-bg',
  send_hover_bg: '--sb-send-hover-bg',
  send_text: '--sb-send-text',
  toggle_bg: '--sb-toggle-bg',
  toggle_hover_bg: '--sb-toggle-hover-bg',
  toggle_text: '--sb-toggle-text',
  toggle_shadow: '--sb-toggle-shadow',
  sliver_bg: '--sb-sliver-bg',
  sliver_hover_bg: '--sb-sliver-hover-bg',
  sliver_shadow_color: '--sb-sliver-shadow-color',
  sliver_shadow_hover_color: '--sb-sliver-shadow-hover-color',
};

const toSupportBotThemeClassName = (productKey = '') => (
  `support-bot-widget--${String(productKey || '').replace(/_/g, '-')}`
);

const mergeThemeConfigWithDefaults = (incomingConfig = {}) => {
  const base = { ...EMPTY_THEME_CONFIG };
  const source = incomingConfig && typeof incomingConfig === 'object' ? incomingConfig : {};

  return Object.keys(base).reduce((acc, key) => {
    const raw = String(source[key] ?? '').trim();
    acc[key] = raw || base[key] || '';
    return acc;
  }, {});
};

const toColorPickerValue = (value, fallback = '#000000') => {
  const cleaned = String(value || '').trim();
  if (!HEX_COLOR_REGEX.test(cleaned)) {
    const gradientHexMatch = cleaned.match(/#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})/);
    if (!gradientHexMatch) return fallback;
    const hexFromGradient = gradientHexMatch[0];
    if (hexFromGradient.length === 4) {
      const [, r, g, b] = hexFromGradient;
      return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
    }
    if (hexFromGradient.length === 9) return hexFromGradient.slice(0, 7).toLowerCase();
    return hexFromGradient.slice(0, 7).toLowerCase();
  }
  if (cleaned.length === 4) {
    const [, r, g, b] = cleaned;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  if (cleaned.length === 9) {
    return cleaned.slice(0, 7).toLowerCase();
  }
  return cleaned.slice(0, 7).toLowerCase();
};

const normalizeTypedHexValue = (value) => {
  const cleaned = String(value || '').trim();
  if (!cleaned) return '';
  if (cleaned.startsWith('#')) return cleaned;
  return `#${cleaned.replace(/^#+/, '')}`;
};

const createPreviewChatMessage = (role, content) => ({
  id: `preview-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  role: role === 'assistant' ? 'assistant' : 'user',
  content: String(content || '').trim(),
});

const toPreviewHistory = (messages = []) => (
  (Array.isArray(messages) ? messages : [])
    .filter((item) => item?.content)
    .slice(-MAX_PREVIEW_HISTORY)
    .map((item) => ({
      role: item.role === 'assistant' ? 'assistant' : 'user',
      content: String(item.content || '').trim(),
    }))
);

const EMPTY_FORM = {
  enabled: false,
  provider: 'gemini',
  welcome_enabled: true,
  welcome_message: 'Oi! Posso te ajudar a usar este sistema?',
  auto_open_on_first_access: true,
  knowledge: null,
  presentation_config: {
    bot_name: 'Assistente',
    header_subtitle: DEFAULT_HEADER_SUBTITLE,
    toggle_label: DEFAULT_TOGGLE_LABEL,
    greeting_style: 'profissional e objetivo',
    emoji_policy: 'usar emojis apenas quando agregar clareza',
    avatar_url: '',
    theme_config: { ...EMPTY_THEME_CONFIG },
  },
  suggested_questions_text: '',
};

const normalizeQuestions = (value) => (
  String(value || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 8)
);

const toFormState = (payload = {}) => {
  const presentation = payload.presentation_config || {};
  const incomingThemeConfig = presentation.theme_config || {};
  const mergedThemeConfig = mergeThemeConfigWithDefaults(incomingThemeConfig);
  const suggested = Array.isArray(payload.suggested_questions)
    ? payload.suggested_questions
    : [];

  return {
    enabled: Boolean(payload.enabled),
    provider: payload.provider || 'gemini',
    welcome_enabled: payload.welcome_enabled !== false,
    welcome_message: payload.welcome_message || EMPTY_FORM.welcome_message,
    auto_open_on_first_access: payload.auto_open_on_first_access !== false,
    knowledge: payload.knowledge || null,
    presentation_config: {
      bot_name: presentation.bot_name || EMPTY_FORM.presentation_config.bot_name,
      header_subtitle: presentation.header_subtitle || DEFAULT_HEADER_SUBTITLE,
      toggle_label: presentation.toggle_label || DEFAULT_TOGGLE_LABEL,
      greeting_style: presentation.greeting_style || EMPTY_FORM.presentation_config.greeting_style,
      emoji_policy: presentation.emoji_policy || EMPTY_FORM.presentation_config.emoji_policy,
      avatar_url: presentation.avatar_url || EMPTY_FORM.presentation_config.avatar_url,
      theme_config: mergedThemeConfig,
    },
    suggested_questions_text: suggested.join('\n'),
  };
};

const PAGE_BG = `
  radial-gradient(ellipse at 0% 0%, rgba(99, 102, 241, 0.07), transparent 45%),
  radial-gradient(ellipse at 100% 0%, rgba(124, 58, 237, 0.05), transparent 45%),
  linear-gradient(180deg, #f7f9ff 0%, #f8fafc 60%, #f7f9ff 100%)
`;

const CARD_SX = {
  borderRadius: '20px',
  border: '1px solid rgba(15, 23, 42, 0.06)',
  boxShadow: '0 14px 36px -14px rgba(15, 23, 42, 0.10), 0 0 0 1px rgba(15, 23, 42, 0.02)',
  background: '#ffffff',
  overflow: 'hidden',
};

const makeIconBox = (color, bg, border) => ({
  width: 36,
  height: 36,
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: bg,
  color,
  flexShrink: 0,
  border: `1px solid ${border}`,
});

const ICON_INDIGO = makeIconBox(
  '#6366f1',
  'linear-gradient(135deg, rgba(99,102,241,0.14) 0%, rgba(124,58,237,0.10) 100%)',
  'rgba(99,102,241,0.18)',
);
const ICON_CYAN = makeIconBox(
  '#0891b2',
  'linear-gradient(135deg, rgba(8,145,178,0.14) 0%, rgba(6,182,212,0.10) 100%)',
  'rgba(8,145,178,0.18)',
);
const ICON_VIOLET = makeIconBox(
  '#7c3aed',
  'linear-gradient(135deg, rgba(124,58,237,0.14) 0%, rgba(139,92,246,0.10) 100%)',
  'rgba(124,58,237,0.18)',
);
const ICON_AMBER = makeIconBox(
  '#b45309',
  'linear-gradient(135deg, rgba(180,83,9,0.12) 0%, rgba(245,158,11,0.08) 100%)',
  'rgba(180,83,9,0.18)',
);

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '12px' } };

const toggleRowSx = {
  px: 1.5,
  py: 1,
  borderRadius: '12px',
  background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
  border: '1px solid rgba(15,23,42,0.08)',
  transition: 'border-color 0.2s ease',
  '&:hover': { borderColor: 'rgba(99,102,241,0.32)' },
};

const BotsPage = () => {
  const [bots, setBots] = useState([]);
  const [selectedProductKey, setSelectedProductKey] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const [previewDraft, setPreviewDraft] = useState('');
  const [previewChatMessages, setPreviewChatMessages] = useState([]);
  const [previewError, setPreviewError] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [previewWidgetOpen, setPreviewWidgetOpen] = useState(false);
  const previewPanelBodyRef = useRef(null);

  const selectedBotSummary = useMemo(
    () => bots.find((bot) => bot.product_key === selectedProductKey) || null,
    [bots, selectedProductKey]
  );
  const previewThemeStyle = useMemo(() => (
    Object.entries(THEME_CONFIG_CSS_VAR_MAP).reduce((acc, [configKey, cssVar]) => {
      const raw = String(form.presentation_config?.theme_config?.[configKey] || '').trim();
      if (raw) acc[cssVar] = raw;
      return acc;
    }, {})
  ), [form.presentation_config?.theme_config]);
  const previewThemeClassName = useMemo(
    () => (selectedProductKey ? toSupportBotThemeClassName(selectedProductKey) : ''),
    [selectedProductKey]
  );
  const previewHeaderSubtitle = useMemo(
    () => form.presentation_config?.header_subtitle || DEFAULT_HEADER_SUBTITLE,
    [form.presentation_config?.header_subtitle]
  );
  const previewToggleLabel = useMemo(
    () => form.presentation_config?.toggle_label || DEFAULT_TOGGLE_LABEL,
    [form.presentation_config?.toggle_label]
  );
  const previewSuggestedQuestions = useMemo(
    () => normalizeQuestions(form.suggested_questions_text),
    [form.suggested_questions_text]
  );

  const buildInitialPreviewMessages = useCallback((_formState) => {
    return [];
  }, []);

  const loadBots = useCallback(async (preferredKey) => {
    setLoadingList(true);
    try {
      const data = await getSupportBots();
      const list = Array.isArray(data) ? data : [];
      setBots(list);
      setSelectedProductKey((prev) => preferredKey || prev || list[0]?.product_key || '');
    } catch (error) {
      console.error('Erro ao carregar bots:', error);
      toast.error('Não foi possível carregar os bots.');
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadBotDetails = useCallback(async (productKey) => {
    if (!productKey) return;
    setLoadingDetails(true);
    setPreviewError('');

    try {
      const data = await getSupportBotByProduct(productKey);
      const nextForm = toFormState(data);
      setForm(nextForm);
      setPreviewDraft('');
      setPreviewChatMessages(buildInitialPreviewMessages(nextForm));
      setPreviewWidgetOpen(false);
    } catch (error) {
      console.error('Erro ao carregar detalhes do bot:', error);
      toast.error('Não foi possível carregar os detalhes do bot.');
    } finally {
      setLoadingDetails(false);
    }
  }, [buildInitialPreviewMessages]);

  useEffect(() => {
    loadBots();
  }, [loadBots]);

  useEffect(() => {
    if (selectedProductKey) {
      loadBotDetails(selectedProductKey);
    } else {
      setForm(toFormState({}));
      setPreviewDraft('');
      setPreviewChatMessages([]);
      setPreviewError('');
      setPreviewWidgetOpen(false);
    }
  }, [selectedProductKey, loadBotDetails, buildInitialPreviewMessages]);

  useEffect(() => {
    if (!previewWidgetOpen) return;
    const node = previewPanelBodyRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [previewWidgetOpen, previewChatMessages]);

  const handleChange = (field) => (event) => {
    const nextValue = event?.target?.value;
    setForm((prev) => ({ ...prev, [field]: nextValue }));
  };

  const handleToggle = (field) => (_event, checked) => {
    setForm((prev) => ({ ...prev, [field]: checked }));
  };

  const handlePresentationChange = (field) => (event) => {
    const nextValue = event?.target?.value || '';
    setForm((prev) => ({
      ...prev,
      presentation_config: {
        ...prev.presentation_config,
        [field]: nextValue,
      },
    }));
  };

  const handleThemeColorChange = (field, options = {}) => (event) => {
    const { forceHexPrefix = false } = options;
    const rawValue = event?.target?.value || '';
    const nextValue = forceHexPrefix ? normalizeTypedHexValue(rawValue) : rawValue;
    setForm((prev) => ({
      ...prev,
      presentation_config: {
        ...prev.presentation_config,
        theme_config: {
          ...mergeThemeConfigWithDefaults(prev.presentation_config?.theme_config || {}),
          [field]: nextValue,
        },
      },
    }));
  };

  const handleThemeColorPick = (field) => (event) => {
    const nextValue = event?.target?.value || '';
    handleThemeColorChange(field)({ target: { value: nextValue } });
  };

  const handleResetThemeConfig = () => {
    setForm((prev) => ({
      ...prev,
      presentation_config: {
        ...prev.presentation_config,
        theme_config: { ...EMPTY_THEME_CONFIG },
      },
    }));
  };

  const buildPayload = () => ({
    enabled: Boolean(form.enabled),
    provider: form.provider || 'gemini',
    welcome_enabled: Boolean(form.welcome_enabled),
    welcome_message: form.welcome_message || '',
    auto_open_on_first_access: Boolean(form.auto_open_on_first_access),
    suggested_questions: normalizeQuestions(form.suggested_questions_text),
    presentation_config: {
      bot_name: form.presentation_config?.bot_name || '',
      header_subtitle: form.presentation_config?.header_subtitle || DEFAULT_HEADER_SUBTITLE,
      toggle_label: form.presentation_config?.toggle_label || DEFAULT_TOGGLE_LABEL,
      greeting_style: form.presentation_config?.greeting_style || '',
      emoji_policy: form.presentation_config?.emoji_policy || '',
      avatar_url: form.presentation_config?.avatar_url || '',
      theme_config: mergeThemeConfigWithDefaults(form.presentation_config?.theme_config || {}),
    },
  });

  const handleSave = async () => {
    if (!selectedProductKey) return;

    setSaving(true);
    try {
      const payload = buildPayload();
      const data = await updateSupportBotByProduct(selectedProductKey, payload);
      setForm(toFormState(data));
      toast.success('Configuração do bot salva com sucesso.');

      setBots((prev) => prev.map((item) => (
        item.product_key === selectedProductKey
          ? {
            ...item,
            enabled: Boolean(data.enabled),
            provider: data.provider || item.provider,
            welcome_enabled: Boolean(data.welcome_enabled),
            updated_at: data.updated_at || item.updated_at,
          }
          : item
      )));
    } catch (error) {
      console.error('Erro ao salvar bot:', error);
      toast.error(error?.response?.data?.error || 'Erro ao salvar configuração do bot.');
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewSend = async (rawMessage = previewDraft) => {
    const nextText = String(rawMessage || '').trim();
    if (!selectedProductKey || !nextText) {
      toast.warning('Digite uma mensagem para testar o bot.');
      return;
    }
    if (previewing) return;

    const historyBeforeSend = toPreviewHistory(previewChatMessages);
    const userMessage = createPreviewChatMessage('user', nextText);
    setPreviewChatMessages((prev) => [...prev, userMessage].slice(-MAX_PREVIEW_CHAT_MESSAGES));
    setPreviewDraft('');
    setPreviewError('');

    setPreviewing(true);
    try {
      const payload = {
        message: nextText,
        history: historyBeforeSend,
        config: buildPayload(),
      };
      const data = await previewSupportBotByProduct(selectedProductKey, payload);
      const responseText = String(data?.response || 'Sem resposta.').trim() || 'Sem resposta.';
      setPreviewChatMessages((prev) => (
        [...prev, createPreviewChatMessage('assistant', responseText)].slice(-MAX_PREVIEW_CHAT_MESSAGES)
      ));
    } catch (error) {
      console.error('Erro ao testar bot:', error);
      setPreviewError(error?.response?.data?.error || 'Erro ao testar configuração do bot.');
    } finally {
      setPreviewing(false);
    }
  };

  const handlePreviewSubmit = (event) => {
    event.preventDefault();
    handlePreviewSend(previewDraft);
  };

  const handleAvatarFileChange = async (event) => {
    const file = event.target?.files?.[0];
    event.target.value = '';
    if (!file || !selectedProductKey) return;

    setUploadingAvatar(true);
    try {
      const data = await uploadSupportBotAvatar(selectedProductKey, file);
      setForm(toFormState(data));
      toast.success('Avatar enviado com sucesso.');

      setBots((prev) => prev.map((item) => (
        item.product_key === selectedProductKey
          ? {
            ...item,
            updated_at: data.updated_at || item.updated_at,
          }
          : item
      )));
    } catch (error) {
      console.error('Erro ao enviar avatar:', error);
      toast.error(error?.response?.data?.error || 'Erro ao enviar avatar.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: PAGE_BG }}>
      <Box sx={{ maxWidth: 1500, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* ── Header card ── */}
        <Box sx={CARD_SX}>
          <Box sx={{ height: 4, background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 60%, #a78bfa 100%)' }} />
          <Box sx={{ p: { xs: 2.5, md: 3 } }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ md: 'center' }}
              spacing={{ xs: 2, md: 0 }}
            >
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Box sx={{ ...makeIconBox('#6366f1', 'linear-gradient(135deg, rgba(99,102,241,0.14) 0%, rgba(124,58,237,0.10) 100%)', 'rgba(99,102,241,0.18)'), width: 48, height: 48, borderRadius: '14px' }}>
                  <SmartToy sx={{ fontSize: 26 }} />
                </Box>
                <Box>
                  <Typography fontWeight={800} sx={{ color: '#0f172a', fontSize: '1.25rem', letterSpacing: '-0.02em', lineHeight: 1.2, mb: 0.25 }}>
                    Bots
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Configure o comportamento do chatbot por produto e teste antes de publicar.
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => loadBots(selectedProductKey)}
                  disabled={loadingList || loadingDetails}
                  sx={{
                    borderRadius: '12px', fontWeight: 600,
                    borderColor: 'rgba(99,102,241,0.30)', color: '#6366f1',
                    '&:hover': { bgcolor: 'rgba(99,102,241,0.06)', borderColor: '#6366f1' },
                  }}
                >
                  Atualizar
                </Button>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                  onClick={handleSave}
                  disabled={!selectedProductKey || loadingDetails || saving}
                  sx={{
                    borderRadius: '12px', fontWeight: 700, boxShadow: 'none',
                    bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5', boxShadow: '0 4px 14px rgba(99,102,241,0.3)' },
                  }}
                >
                  Salvar
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>

        {/* ── Main content ── */}
        {loadingList ? (
          <Box sx={CARD_SX}>
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <CircularProgress sx={{ color: '#6366f1' }} />
            </Box>
          </Box>
        ) : (
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="stretch">

            {/* ── Products sidebar ── */}
            <Box sx={{ ...CARD_SX, width: { xs: '100%', lg: '280px' }, flexShrink: 0 }}>
              <Box sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" gap={1.25} mb={1.5}>
                  <Box sx={ICON_INDIGO}><ViewList sx={{ fontSize: 18 }} /></Box>
                  <Typography fontWeight={700} sx={{ color: '#0f172a', fontSize: '0.975rem', letterSpacing: '-0.01em' }}>
                    Produtos
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 1.5, borderColor: 'rgba(15,23,42,0.06)' }} />
                <List disablePadding>
                  {bots.map((item) => (
                    <ListItemButton
                      key={item.product_key}
                      selected={item.product_key === selectedProductKey}
                      onClick={() => setSelectedProductKey(item.product_key)}
                      sx={{
                        borderRadius: '12px',
                        mb: 0.5,
                        transition: 'all 0.15s ease',
                        '&.Mui-selected': {
                          bgcolor: 'rgba(99,102,241,0.10)',
                          '&:hover': { bgcolor: 'rgba(99,102,241,0.14)' },
                        },
                        '&:hover': { bgcolor: 'rgba(99,102,241,0.06)' },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography fontWeight={600} sx={{ fontSize: '0.9rem', color: '#0f172a' }}>
                            {item.display_name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            {item.product_key}
                          </Typography>
                        }
                      />
                      <Chip
                        size="small"
                        label={item.enabled ? 'Ativo' : 'Inativo'}
                        sx={item.enabled ? {
                          bgcolor: 'rgba(5,150,105,0.10)',
                          color: '#059669',
                          fontWeight: 700,
                          borderRadius: '8px',
                          border: '1px solid rgba(5,150,105,0.20)',
                          fontSize: '0.72rem',
                        } : {
                          bgcolor: 'rgba(15,23,42,0.06)',
                          color: '#64748b',
                          fontWeight: 600,
                          borderRadius: '8px',
                          border: '1px solid rgba(15,23,42,0.10)',
                          fontSize: '0.72rem',
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            </Box>

            {/* ── Details card ── */}
            <Box sx={{ ...CARD_SX, flex: 1 }}>
              {!selectedProductKey ? (
                <Box sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Alert severity="info" sx={{ borderRadius: '12px' }}>
                    Selecione um produto para editar o bot.
                  </Alert>
                </Box>
              ) : loadingDetails ? (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                  <CircularProgress sx={{ color: '#6366f1' }} />
                </Box>
              ) : (
                <>
                  <Box sx={{ height: 4, background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 60%, #a78bfa 100%)' }} />
                  <Box sx={{ p: { xs: 2.5, md: 3 } }}>

                    {/* Details header */}
                    <Stack direction="row" alignItems="center" gap={1.5} mb={3}>
                      <Box sx={{ ...makeIconBox('#6366f1', 'linear-gradient(135deg, rgba(99,102,241,0.14) 0%, rgba(124,58,237,0.10) 100%)', 'rgba(99,102,241,0.18)'), width: 40, height: 40, borderRadius: '12px' }}>
                        <SmartToy sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography fontWeight={800} sx={{ color: '#0f172a', fontSize: '1.1rem', letterSpacing: '-0.02em' }}>
                        {selectedBotSummary?.display_name || selectedProductKey}
                      </Typography>
                    </Stack>

                    <Stack spacing={3.5}>

                      {/* ── Section: Comportamento ── */}
                      <Box>
                        <Stack direction="row" alignItems="center" gap={1.25} mb={1}>
                          <Box sx={ICON_INDIGO}><Tune sx={{ fontSize: 18 }} /></Box>
                          <Box>
                            <Typography fontWeight={700} sx={{ color: '#0f172a', fontSize: '0.95rem' }}>Comportamento</Typography>
                            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.82rem' }}>Ativação, provider e regras de exibição.</Typography>
                          </Box>
                        </Stack>
                        <Divider sx={{ mb: 2, borderColor: 'rgba(15,23,42,0.06)' }} />
                        <Stack spacing={1.5}>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                            <Box sx={{ ...toggleRowSx, flex: 1 }}>
                              <FormControlLabel
                                control={<Switch checked={Boolean(form.enabled)} onChange={handleToggle('enabled')} size="small" color="primary" />}
                                label={<Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#0f172a' }}>Bot habilitado</Typography>}
                                labelPlacement="start"
                                sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                              />
                            </Box>
                            <Box sx={{ ...toggleRowSx, flex: 1 }}>
                              <FormControlLabel
                                control={<Switch checked={Boolean(form.welcome_enabled)} onChange={handleToggle('welcome_enabled')} size="small" color="primary" />}
                                label={<Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#0f172a' }}>Mensagem inicial habilitada</Typography>}
                                labelPlacement="start"
                                sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                              />
                            </Box>
                            <Box sx={{ ...toggleRowSx, flex: 1 }}>
                              <FormControlLabel
                                control={<Switch checked={Boolean(form.auto_open_on_first_access)} onChange={handleToggle('auto_open_on_first_access')} size="small" color="primary" />}
                                label={<Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#0f172a' }}>Abrir automaticamente no primeiro acesso</Typography>}
                                labelPlacement="start"
                                sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                              />
                            </Box>
                          </Stack>
                          <FormControl fullWidth sx={fieldSx}>
                            <InputLabel id="bot-provider-label">Provider</InputLabel>
                            <Select
                              labelId="bot-provider-label"
                              label="Provider"
                              value={form.provider}
                              onChange={handleChange('provider')}
                            >
                              <MenuItem value="gemini">Gemini</MenuItem>
                              <MenuItem value="openai">OpenAI</MenuItem>
                            </Select>
                          </FormControl>
                        </Stack>
                      </Box>

                      {/* ── Section: Apresentação ── */}
                      <Box>
                        <Stack direction="row" alignItems="center" gap={1.25} mb={1}>
                          <Box sx={ICON_CYAN}><ChatBubbleOutlineOutlined sx={{ fontSize: 18 }} /></Box>
                          <Box>
                            <Typography fontWeight={700} sx={{ color: '#0f172a', fontSize: '0.95rem' }}>Apresentação</Typography>
                            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.82rem' }}>Aparência e textos do widget de chat.</Typography>
                          </Box>
                        </Stack>
                        <Divider sx={{ mb: 2, borderColor: 'rgba(15,23,42,0.06)' }} />
                        <Stack spacing={2}>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Subtítulo do cabeçalho"
                              value={form.presentation_config.header_subtitle}
                              onChange={handlePresentationChange('header_subtitle')}
                              sx={fieldSx}
                            />
                            <TextField
                              fullWidth
                              label="Texto do botão de abrir chat"
                              value={form.presentation_config.toggle_label}
                              onChange={handlePresentationChange('toggle_label')}
                              sx={fieldSx}
                            />
                          </Stack>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                            <TextField
                              fullWidth
                              label="Avatar URL (opcional)"
                              value={form.presentation_config.avatar_url}
                              onChange={handlePresentationChange('avatar_url')}
                              helperText="Se vazio, o widget mostra apenas o nome/inicial do personagem."
                              sx={fieldSx}
                            />
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Avatar
                                src={form.presentation_config.avatar_url || undefined}
                                alt={form.presentation_config.bot_name || 'Assistente'}
                                sx={{
                                  width: 42,
                                  height: 42,
                                  bgcolor: '#6366f1',
                                  fontWeight: 700,
                                  boxShadow: '0 4px 12px rgba(99,102,241,0.25)',
                                }}
                              >
                                {(form.presentation_config.bot_name || 'A').trim().slice(0, 1).toUpperCase()}
                              </Avatar>
                              <Button
                                component="label"
                                variant="outlined"
                                disabled={!selectedProductKey || uploadingAvatar}
                                startIcon={uploadingAvatar ? <CircularProgress size={16} color="inherit" /> : <CloudUploadOutlined />}
                                sx={{
                                  borderRadius: '12px', fontWeight: 600,
                                  borderColor: 'rgba(99,102,241,0.30)', color: '#6366f1',
                                  '&:hover': { bgcolor: 'rgba(99,102,241,0.06)', borderColor: '#6366f1' },
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Upload
                                <input
                                  hidden
                                  type="file"
                                  accept="image/png,image/jpeg,image/webp"
                                  onChange={handleAvatarFileChange}
                                />
                              </Button>
                            </Stack>
                          </Stack>
                          <TextField
                            fullWidth
                            label="Mensagem de boas-vindas"
                            value={form.welcome_message}
                            onChange={handleChange('welcome_message')}
                            sx={fieldSx}
                          />
                        </Stack>
                      </Box>

                      {/* ── Section: Estilo do chat ── */}
                      <Box>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                          <Stack direction="row" alignItems="center" gap={1.25}>
                            <Box sx={ICON_VIOLET}><Palette sx={{ fontSize: 18 }} /></Box>
                            <Box>
                              <Typography fontWeight={700} sx={{ color: '#0f172a', fontSize: '0.95rem' }}>Estilo do chat</Typography>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.82rem' }}>Cores personalizadas por produto.</Typography>
                            </Box>
                          </Stack>
                          <Button
                            variant="text"
                            size="small"
                            onClick={handleResetThemeConfig}
                            sx={{
                              borderRadius: '10px', fontWeight: 600, fontSize: '0.8rem',
                              color: '#be123c',
                              '&:hover': { bgcolor: '#fff1f2' },
                            }}
                          >
                            Limpar cores
                          </Button>
                        </Stack>
                        <Divider sx={{ mb: 2, borderColor: 'rgba(15,23,42,0.06)' }} />
                        <Box
                          display="grid"
                          gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                          gap={2}
                        >
                          {THEME_COLOR_FIELDS.map((field) => (
                            field.allowGradient ? (
                              <TextField
                                key={field.key}
                                fullWidth
                                label={field.label}
                                placeholder="linear-gradient(...) ou #RRGGBB"
                                value={form.presentation_config?.theme_config?.[field.key] || ''}
                                onChange={handleThemeColorChange(field.key)}
                                helperText="Aceita #RRGGBB ou linear-gradient(...)."
                                sx={fieldSx}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Box
                                        component="input"
                                        type="color"
                                        className="bots-page__color-picker-inline"
                                        aria-label={`Selecionar ${field.label}`}
                                        value={toColorPickerValue(form.presentation_config?.theme_config?.[field.key])}
                                        onChange={handleThemeColorPick(field.key)}
                                        onClick={(event) => event.stopPropagation()}
                                      />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            ) : (
                              <TextField
                                key={field.key}
                                fullWidth
                                type="text"
                                label={field.label}
                                placeholder="#RRGGBB"
                                value={form.presentation_config?.theme_config?.[field.key] || ''}
                                onChange={handleThemeColorChange(field.key, { forceHexPrefix: true })}
                                helperText="Digite #RRGGBB ou use o seletor no fim do campo."
                                sx={fieldSx}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Box
                                        component="input"
                                        type="color"
                                        className="bots-page__color-picker-inline"
                                        aria-label={`Selecionar ${field.label}`}
                                        value={toColorPickerValue(form.presentation_config?.theme_config?.[field.key])}
                                        onChange={handleThemeColorPick(field.key)}
                                        onClick={(event) => event.stopPropagation()}
                                      />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )
                          ))}
                        </Box>
                      </Box>

                      {/* ── Section: Perguntas sugeridas ── */}
                      <Box>
                        <Stack direction="row" alignItems="center" gap={1.25} mb={1}>
                          <Box sx={ICON_AMBER}><HelpOutline sx={{ fontSize: 18 }} /></Box>
                          <Box>
                            <Typography fontWeight={700} sx={{ color: '#0f172a', fontSize: '0.95rem' }}>Perguntas sugeridas</Typography>
                            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.82rem' }}>Máximo de 8 perguntas, uma por linha.</Typography>
                          </Box>
                        </Stack>
                        <Divider sx={{ mb: 2, borderColor: 'rgba(15,23,42,0.06)' }} />
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          label="Perguntas sugeridas (1 por linha)"
                          value={form.suggested_questions_text}
                          onChange={handleChange('suggested_questions_text')}
                          helperText="Máximo de 8 perguntas."
                          sx={fieldSx}
                        />
                      </Box>

                      {/* ── Section: Base de conhecimento ── */}
                      <Box>
                        <Stack direction="row" alignItems="center" mb={1}>
                          <Stack direction="row" alignItems="center" gap={1.25}>
                            <Box sx={ICON_INDIGO}><ViewList sx={{ fontSize: 18 }} /></Box>
                            <Box>
                              <Typography fontWeight={700} sx={{ color: '#0f172a', fontSize: '0.95rem' }}>Base de conhecimento</Typography>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.82rem' }}>
                                A documentação do produto é versionada junto ao código.
                              </Typography>
                            </Box>
                          </Stack>
                        </Stack>
                        <Divider sx={{ mb: 2, borderColor: 'rgba(15,23,42,0.06)' }} />
                        <Stack spacing={1.5}>
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Chip
                              size="small"
                              color="primary"
                              variant="outlined"
                              label="Docs-as-code"
                            />
                            {form.knowledge?.version ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Versão ${form.knowledge.version}`}
                              />
                            ) : null}
                          </Stack>
                          {Array.isArray(form.knowledge?.documents) ? (
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                              {form.knowledge.documents.map((documentName) => (
                                <Chip key={documentName} size="small" label={documentName} />
                              ))}
                            </Stack>
                          ) : null}
                          <Alert severity="info" sx={{ borderRadius: '12px' }}>
                            O conhecimento funcional não é editado pelo Manager. Nome, aparência,
                            boas-vindas, comportamento e perguntas sugeridas continuam configuráveis aqui.
                          </Alert>
                        </Stack>
                      </Box>

                    </Stack>
                  </Box>
                </>
              )}
            </Box>
          </Stack>
        )}

        {/* ── Chat preview widget (floating) ── */}
        {selectedProductKey ? (
          <SupportBotChatSurface
            isOpen={previewWidgetOpen}
            onToggle={() => setPreviewWidgetOpen((prev) => !prev)}
            loading={false}
            sending={previewing}
            errorText={previewError}
            welcomeMessage={form.welcome_enabled ? form.welcome_message : ''}
            messages={previewChatMessages}
            suggestedQuestions={previewSuggestedQuestions}
            showSuggestedQuestionsOnlyAtStart
            onSuggestedQuestion={handlePreviewSend}
            draft={previewDraft}
            onDraftChange={setPreviewDraft}
            onSubmit={handlePreviewSubmit}
            unreadCount={0}
            botName={form.presentation_config.bot_name || 'Assistente'}
            avatarUrl={form.presentation_config.avatar_url || ''}
            headerSubtitle={previewHeaderSubtitle}
            toggleLabel={previewToggleLabel}
            themeClassName={previewThemeClassName}
            themeStyle={previewThemeStyle}
            messagesContainerRef={previewPanelBodyRef}
          />
        ) : null}

      </Box>
    </Box>
  );
};

export default BotsPage;